import React, { useState, useRef } from 'react';
import { exportElementToPdf } from '../../lib/pdfExport';
import { generateTeardownMarkdown } from '../../lib/toolCalculations';

interface TeardownLabProps {
  isUnlocked?: boolean;
  onOpenCheckout?: (tier?: 'single' | 'bundle') => void;
}

export const TeardownLab: React.FC<TeardownLabProps> = ({
  isUnlocked = false,
  onOpenCheckout,
}) => {
  const documentRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [targetCompany, setTargetCompany] = useState('OmniFlow Logistics');
  const [targetUrl, setTargetUrl] = useState('app.omniflow.io');
  const [auditVersion, setAuditVersion] = useState('v1.2 Final');
  const [targetEnvironment, setTargetEnvironment] = useState('AWS us-east-1 · Production VPC (main@c49ab1f)');
  const [samplingWindow, setSamplingWindow] = useState('72-Hour Continuous Profiling & AST Audit');
  const [targetSla, setTargetSla] = useState('< 400ms p95 · 99.95% Availability');
  const [techStack, setTechStack] = useState('React, Node.js API, PostgreSQL on AWS RDS');
  const [failure1, setFailure1] = useState('Unindexed SQL join queries causing 3.8s latency on the core shipping table.');
  const [failure2, setFailure2] = useState('Uncached client-side state fetching full payloads on every page re-render.');
  const [failure3, setFailure3] = useState('Missing edge CDN caching leading to $2,800/mo in redundant EC2 compute overages.');
  const [estimatedLoss, setEstimatedLoss] = useState(14500);
  const [remediationPrice, setRemediationPrice] = useState(9500);
  const [copied, setCopied] = useState(false);
  const isWatermarked = !isUnlocked;

  const handleCopyMarkdown = () => {
    const md = generateTeardownMarkdown({
      targetCompany,
      targetUrl,
      auditVersion,
      targetEnvironment,
      samplingWindow,
      targetSla,
      techStack,
      failure1,
      failure2,
      failure3,
      estimatedLoss,
      remediationPrice,
      isWatermarked,
    });

    navigator.clipboard.writeText(md);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExportPdf = async () => {
    try {
      setIsExporting(true);
      await exportElementToPdf(documentRef.current, {
        filename: `Due-Diligence-Audit-${targetCompany.replace(/[^a-zA-Z0-9_-]/g, '-')}-MEMO-2026-AUDIT-084.pdf`,
        documentTitle: `Architecture Teardown · ${targetCompany}`,
        isUnlocked,
      });
    } catch (err) {
      console.error('PDF export failed:', err);
      window.print();
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Top Banner (Hidden in PDF / Print) */}
      <div className="no-print screen-only flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[#232736] pb-6">
        <div>
          <div className="text-[11px] font-mono text-sky-400 uppercase tracking-widest flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-sky-400" />
            TOOL 03 // TEARDOWN LAB &amp; AUDIT REPORT GENERATOR
          </div>
          <h2 className="font-tech text-3xl font-bold text-white mt-1">
            Executive Architecture Audit Generator
          </h2>
          <p className="text-xs text-[#8b92a5] font-mono mt-1">
            Compile executive teardowns with failure-point risk matrices to win $10,000+ consulting sprints.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {isUnlocked ? (
            <span className="px-2.5 py-1 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-mono text-[10px] font-bold flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              PRO UNLOCKED
            </span>
          ) : (
            <button
              onClick={() => onOpenCheckout?.('single')}
              className="px-2.5 py-1.5 rounded bg-sky-400 hover:bg-sky-300 text-black font-mono text-[10px] font-bold transition flex items-center gap-1.5 shadow-[0_0_10px_rgba(56,189,248,0.2)]"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-black animate-pulse" />
              UNLOCK PRO ($9)
            </button>
          )}
          <button
            onClick={handleCopyMarkdown}
            className="px-3 py-1.5 bg-[#181b26] border border-[#232736] hover:border-sky-500/40 text-xs font-mono text-white rounded transition flex items-center gap-1.5"
          >
            {copied ? '✓ Copied Markdown' : 'Copy Report MD'}
          </button>
          <button
            onClick={handleExportPdf}
            disabled={isExporting}
            className="px-3.5 py-1.5 bg-sky-400 hover:bg-sky-300 disabled:opacity-50 text-black font-tech font-bold text-xs rounded transition flex items-center gap-1.5 shadow-[0_0_12px_rgba(56,189,248,0.25)]"
          >
            {isExporting ? (
              <>
                <span className="w-3 h-3 border-2 border-black border-t-transparent rounded-full animate-spin" />
                Generating PDF...
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {isUnlocked ? 'Download Vector Audit PDF' : 'Download Audit PDF (Free)'}
              </>
            )}
          </button>
          <button
            onClick={() => window.print()}
            title="Print to physical printer"
            className="px-2.5 py-1.5 bg-[#181b26] border border-[#232736] hover:border-sky-500/40 text-xs font-mono text-[#8b92a5] hover:text-white rounded transition flex items-center gap-1"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4H9v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Input Column */}
        <div className="lg:col-span-5 space-y-5 no-print">
          <div className="blueprint-card rounded-xl p-4 border border-[#232736] flex items-center justify-between font-mono text-xs">
            <span className="text-[11px] text-[#8b92a5] uppercase">Vector PDF Status:</span>
            {isUnlocked ? (
              <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 font-bold">
                Pro Unbranded (Active)
              </span>
            ) : (
              <button
                onClick={() => onOpenCheckout?.('single')}
                className="text-[10px] px-2 py-0.5 rounded bg-sky-500/20 hover:bg-sky-500/30 text-sky-300 border border-sky-500/40 transition font-bold"
              >
                Watermarked · Unlock Pro ($9)
              </button>
            )}
          </div>
          <div className="blueprint-card rounded-xl p-5 border border-[#232736] space-y-4">
            <h3 className="font-tech text-base font-bold text-white border-b border-[#232736] pb-2">
              Audit Target & Findings
            </h3>

            <div className="space-y-3 font-mono text-xs">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-[#8b92a5] uppercase block mb-1">
                    Target Entity
                  </label>
                  <input
                    type="text"
                    value={targetCompany}
                    onChange={(e) => setTargetCompany(e.target.value)}
                    className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-[#232736] text-white focus:outline-none focus:border-sky-400"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-[#8b92a5] uppercase block mb-1">
                    Target Domain
                  </label>
                  <input
                    type="text"
                    value={targetUrl}
                    onChange={(e) => setTargetUrl(e.target.value)}
                    className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-[#232736] text-white focus:outline-none focus:border-sky-400"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-[#8b92a5] uppercase block mb-1">
                    Audit Version / Rev
                  </label>
                  <input
                    type="text"
                    value={auditVersion}
                    onChange={(e) => setAuditVersion(e.target.value)}
                    className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-[#232736] text-white focus:outline-none focus:border-sky-400"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-[#8b92a5] uppercase block mb-1">
                    Target Environment
                  </label>
                  <input
                    type="text"
                    value={targetEnvironment}
                    onChange={(e) => setTargetEnvironment(e.target.value)}
                    className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-[#232736] text-white focus:outline-none focus:border-sky-400"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] text-[#8b92a5] uppercase block mb-1">
                  Observed Tech Stack
                </label>
                <input
                  type="text"
                  value={techStack}
                  onChange={(e) => setTechStack(e.target.value)}
                  className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-[#232736] text-white focus:outline-none focus:border-sky-400"
                />
              </div>

              <div className="border-t border-[#232736] pt-3 space-y-2">
                <label className="text-[10px] text-red-400 uppercase font-bold block">
                  Failure Point 1 (Critical Vulnerability)
                </label>
                <textarea
                  rows={2}
                  value={failure1}
                  onChange={(e) => setFailure1(e.target.value)}
                  className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-red-500/30 text-white focus:outline-none focus:border-red-400 resize-none text-[11px]"
                />

                <label className="text-[10px] text-amber-400 uppercase font-bold block">
                  Failure Point 2 (Performance / Scale)
                </label>
                <textarea
                  rows={2}
                  value={failure2}
                  onChange={(e) => setFailure2(e.target.value)}
                  className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-amber-500/30 text-white focus:outline-none focus:border-amber-400 resize-none text-[11px]"
                />

                <label className="text-[10px] text-sky-400 uppercase font-bold block">
                  Failure Point 3 (Infrastructure Cost)
                </label>
                <textarea
                  rows={2}
                  value={failure3}
                  onChange={(e) => setFailure3(e.target.value)}
                  className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-sky-500/30 text-white focus:outline-none focus:border-sky-400 resize-none text-[11px]"
                />
              </div>

              <div className="grid grid-cols-2 gap-2 border-t border-[#232736] pt-3">
                <div>
                  <label className="text-[10px] text-[#8b92a5] uppercase block mb-1">
                    Monthly Loss Est. ($)
                  </label>
                  <input
                    type="number"
                    value={estimatedLoss}
                    onChange={(e) => setEstimatedLoss(Number(e.target.value))}
                    className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-[#232736] text-white focus:outline-none focus:border-sky-400"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-[#8b92a5] uppercase block mb-1">
                    Remediation Price ($)
                  </label>
                  <input
                    type="number"
                    value={remediationPrice}
                    onChange={(e) => setRemediationPrice(Number(e.target.value))}
                    className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-[#232736] text-white focus:outline-none focus:border-sky-400"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between border-t border-[#232736] pt-3">
                <span className="text-[11px] text-[#8b92a5] uppercase">Watermark:</span>
                {isUnlocked ? (
                  <span className="text-[10px] px-2 py-0.5 rounded font-mono bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 font-bold">
                    Pro (Removed)
                  </span>
                ) : (
                  <span className="text-[10px] px-2 py-0.5 rounded font-mono bg-zinc-800 text-zinc-400">
                    Watermarked (Free)
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Live Executive Report Preview */}
        <div className="lg:col-span-7">
          <div ref={documentRef} className="print-content blueprint-card rounded-2xl p-6 sm:p-8 border border-sky-500/30 space-y-6 bg-[#0c0e14]">
            {/* Executive Document Header & Letterhead */}
            <div className="print-doc-header border-b border-[#232736] pb-5 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <span className="text-[10px] font-mono text-red-400 uppercase tracking-widest font-bold block">
                    CONFIDENTIAL // TECHNICAL DUE DILIGENCE &amp; AUDIT
                  </span>
                  <h3 className="font-tech text-2xl sm:text-3xl font-bold text-white mt-0.5">
                    Executive Architecture Teardown
                  </h3>
                </div>
                <div className="text-right font-mono text-xs">
                  <span className="text-[10px] text-[#8b92a5] uppercase block">DOCUMENT REF</span>
                  <span className="text-white font-bold tracking-wider">MEMO-2026-AUDIT-084</span>
                  <span className="text-[10px] text-zinc-500 block">REV: {auditVersion}</span>
                </div>
              </div>

              {/* Metadata Bar */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-[#232736] text-[10px] font-mono text-[#8b92a5]">
                <div>
                  <span className="block text-zinc-500 uppercase">Audit Date:</span>
                  <span className="text-zinc-300 font-bold">{new Date().toLocaleDateString()}</span>
                </div>
                <div>
                  <span className="block text-zinc-500 uppercase">Target Entity:</span>
                  <span className="text-zinc-300 font-bold">{targetCompany}</span>
                </div>
                <div>
                  <span className="block text-zinc-500 uppercase">Target Domain:</span>
                  <span className="text-zinc-300 font-bold">{targetUrl}</span>
                </div>
                <div>
                  <span className="block text-zinc-500 uppercase">Classification:</span>
                  <span className="text-red-400 font-bold">Restricted Due Diligence</span>
                </div>
              </div>

              {/* Assessment Parameters & Telemetry Scope */}
              <div className="p-3.5 rounded-lg bg-[#10121a] border border-[#232736] space-y-2 text-xs font-mono">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 border-b border-[#232736]/60 pb-1.5">
                  <span className="text-[10px] text-zinc-400 uppercase font-bold tracking-wider">
                    ASSESSMENT PARAMETERS &amp; SCOPE BOUNDARIES
                  </span>
                  <span className="text-[10px] text-sky-400">Practice: Systems Architecture &amp; Reliability</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
                  <div>
                    <span className="text-zinc-500 block text-[10px] uppercase">Target Environment:</span>
                    <span className="text-zinc-200 font-bold">{targetEnvironment}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500 block text-[10px] uppercase">Sampling Methodology:</span>
                    <span className="text-zinc-200">{samplingWindow}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500 block text-[10px] uppercase">Audited Core Stack:</span>
                    <span className="text-zinc-200">{techStack}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500 block text-[10px] uppercase">Target SLA Benchmark:</span>
                    <span className="text-emerald-400 font-bold">{targetSla}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Executive Scorecard */}
            <div className="print-card print-avoid-break grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="p-3.5 rounded-xl bg-red-500/10 border border-red-500/30">
                <span className="text-[10px] font-mono text-red-400 uppercase font-bold block">
                  RELIABILITY ASSESSMENT
                </span>
                <span className="font-mono text-base font-bold text-white block mt-0.5">
                  GRADE C-
                </span>
                <span className="text-[10px] font-sans text-zinc-400 block mt-0.5">
                  Critical Production Bottlenecks Detected
                </span>
              </div>

              <div className="p-3.5 rounded-xl bg-red-500/10 border border-red-500/30">
                <span className="text-[10px] font-mono text-red-400 uppercase font-bold block">
                  ANNUAL INACTION EXPOSURE
                </span>
                <span className="font-mono text-base font-bold text-red-400 block mt-0.5">
                  ${(estimatedLoss * 12).toLocaleString()}/yr
                </span>
                <span className="text-[10px] font-sans text-zinc-400 block mt-0.5">
                  ${estimatedLoss.toLocaleString()}/mo ongoing commercial drag
                </span>
              </div>

              <div className="p-3.5 rounded-xl bg-sky-500/10 border border-sky-500/30">
                <span className="text-[10px] font-mono text-sky-400 uppercase font-bold block">
                  REMEDIATION TIMEFRAME
                </span>
                <span className="font-mono text-base font-bold text-sky-300 block mt-0.5">
                  10 Working Days
                </span>
                <span className="text-[10px] font-sans text-zinc-400 block mt-0.5">
                  Dedicated Sprint Queue (Single Client)
                </span>
              </div>
            </div>

            {/* Severity Definitions Legend */}
            <div className="print-card print-avoid-break p-3.5 rounded-xl bg-[#10121a] border border-[#232736] space-y-2">
              <span className="text-[10px] font-mono text-[#8b92a5] uppercase tracking-wider font-bold block">
                AUDIT SEVERITY CLASSIFICATION BENCHMARKS
              </span>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] px-1.5 py-0.2 rounded bg-red-500/10 text-red-400 border border-red-500/30 font-mono font-bold">
                      CRITICAL
                    </span>
                  </div>
                  <p className="font-sans text-[11px] text-zinc-400 leading-normal">
                    Immediate data integrity risk, revenue leakage, or core service unavailability (&gt; 2s p95 / complete transaction failure). Immediate triage required.
                  </p>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] px-1.5 py-0.2 rounded bg-amber-500/10 text-amber-400 border border-amber-500/30 font-mono font-bold">
                      HIGH
                    </span>
                  </div>
                  <p className="font-sans text-[11px] text-zinc-400 leading-normal">
                    Latency degradation impacting user retention, memory exhaustion, or elevated operational compute drag. Remediate within dedicated sprint.
                  </p>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] px-1.5 py-0.2 rounded bg-sky-500/10 text-sky-400 border border-sky-500/30 font-mono font-bold">
                      MODERATE
                    </span>
                  </div>
                  <p className="font-sans text-[11px] text-zinc-400 leading-normal">
                    Sub-optimal caching, excess bandwidth/compute egress cost, or unindexed non-critical query paths. Optimize during hardening phase.
                  </p>
                </div>
              </div>
            </div>

            {/* Structured Risk Matrix Table */}
            <div className="print-card print-avoid-break space-y-2">
              <span className="text-[10px] font-mono text-sky-400 uppercase tracking-wider font-bold block">
                IDENTIFIED VULNERABILITIES &amp; RISK MITIGATION MATRIX
              </span>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[#232736] text-[10px] font-mono text-[#8b92a5]">
                    <th className="text-left py-2 px-3 bg-[#10121a] border border-[#232736]">SUBSYSTEM / LAYER</th>
                    <th className="text-left py-2 px-3 bg-[#10121a] border border-[#232736]">OBSERVED DEFECT &amp; FAILURE POINT</th>
                    <th className="text-left py-2 px-3 bg-[#10121a] border border-[#232736]">SEVERITY</th>
                    <th className="text-left py-2 px-3 bg-[#10121a] border border-[#232736]">COMMERCIAL EXPOSURE</th>
                    <th className="text-left py-2 px-3 bg-[#10121a] border border-[#232736]">24H MITIGATION DIRECTIVE</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-[#232736]">
                    <td className="py-2.5 px-3 border border-[#232736] text-white font-mono font-bold align-top shrink-0 text-xs">
                      Database Architecture
                    </td>
                    <td className="py-2.5 px-3 border border-[#232736] font-sans text-xs text-zinc-300 leading-relaxed align-top">
                      {failure1}
                    </td>
                    <td className="py-2.5 px-3 border border-[#232736] align-top">
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/30 font-mono font-bold whitespace-nowrap">
                        CRITICAL
                      </span>
                    </td>
                    <td className="py-2.5 px-3 border border-[#232736] font-sans text-xs text-zinc-400 align-top leading-normal">
                      $6,000/mo cart &amp; query abandonment
                    </td>
                    <td className="py-2.5 px-3 border border-[#232736] font-sans text-xs text-sky-300 align-top leading-normal">
                      Add composite indices on foreign keys; analyze query plan
                    </td>
                  </tr>
                  <tr className="border-b border-[#232736]">
                    <td className="py-2.5 px-3 border border-[#232736] text-white font-mono font-bold align-top shrink-0 text-xs">
                      Client State &amp; Hydration
                    </td>
                    <td className="py-2.5 px-3 border border-[#232736] font-sans text-xs text-zinc-300 leading-relaxed align-top">
                      {failure2}
                    </td>
                    <td className="py-2.5 px-3 border border-[#232736] align-top">
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/30 font-mono font-bold whitespace-nowrap">
                        HIGH
                      </span>
                    </td>
                    <td className="py-2.5 px-3 border border-[#232736] font-sans text-xs text-zinc-400 align-top leading-normal">
                      Mobile bounce rate &amp; UI thread blocking
                    </td>
                    <td className="py-2.5 px-3 border border-[#232736] font-sans text-xs text-sky-300 align-top leading-normal">
                      Implement SWR / React Query caching with stale-while-revalidate
                    </td>
                  </tr>
                  <tr className="border-b border-[#232736]">
                    <td className="py-2.5 px-3 border border-[#232736] text-white font-mono font-bold align-top shrink-0 text-xs">
                      Edge Delivery &amp; CDN
                    </td>
                    <td className="py-2.5 px-3 border border-[#232736] font-sans text-xs text-zinc-300 leading-relaxed align-top">
                      {failure3}
                    </td>
                    <td className="py-2.5 px-3 border border-[#232736] align-top">
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-400 border border-sky-500/30 font-mono font-bold whitespace-nowrap">
                        MODERATE
                      </span>
                    </td>
                    <td className="py-2.5 px-3 border border-[#232736] font-sans text-xs text-zinc-400 align-top leading-normal">
                      $2,800/mo unoptimized compute egress
                    </td>
                    <td className="py-2.5 px-3 border border-[#232736] font-sans text-xs text-sky-300 align-top leading-normal">
                      Deploy Cloudflare Cache Rules &amp; edge compression
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* 3-Phase Implementation Sprint Roadmap */}
            <div className="print-card print-avoid-break space-y-2.5">
              <span className="text-[10px] font-mono text-sky-400 uppercase tracking-wider font-bold block">
                3-PHASE REMEDIATION ROADMAP (10 WORKING DAYS)
              </span>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[#232736] text-[10px] font-mono text-[#8b92a5]">
                    <th className="text-left py-2 px-3 bg-[#10121a] border border-[#232736]">PHASE &amp; TIMELINE</th>
                    <th className="text-left py-2 px-3 bg-[#10121a] border border-[#232736]">CORE ENGINEERING DIRECTIVES</th>
                    <th className="text-left py-2 px-3 bg-[#10121a] border border-[#232736]">VERIFIABLE SUCCESS CRITERIA</th>
                  </tr>
                </thead>
                <tbody className="font-sans text-xs text-zinc-300">
                  <tr className="border-b border-[#232736]">
                    <td className="py-2.5 px-3 border border-[#232736] font-mono font-bold align-top text-white text-xs whitespace-nowrap">
                      Phase 1 (Days 1–3)
                      <span className="block text-[10px] text-red-400 font-normal">Urgent Triage &amp; DB Layer</span>
                    </td>
                    <td className="py-2.5 px-3 border border-[#232736] align-top leading-relaxed">
                      Deploy composite indices on foreign keys, rewrite recursive SQL joins, optimize RDS connection pool parameters, and eliminate sequential table scans.
                    </td>
                    <td className="py-2.5 px-3 border border-[#232736] font-mono text-[11px] text-emerald-400 align-top">
                      p95 query latency &lt; 200ms on core shipping tables
                    </td>
                  </tr>
                  <tr className="border-b border-[#232736]">
                    <td className="py-2.5 px-3 border border-[#232736] font-mono font-bold align-top text-white text-xs whitespace-nowrap">
                      Phase 2 (Days 4–7)
                      <span className="block text-[10px] text-amber-400 font-normal">Client State &amp; Hydration</span>
                    </td>
                    <td className="py-2.5 px-3 border border-[#232736] align-top leading-relaxed">
                      Implement SWR / React Query data caching with stale-while-revalidate, eliminate redundant component re-renders, and partition JavaScript bundle boundaries.
                    </td>
                    <td className="py-2.5 px-3 border border-[#232736] font-mono text-[11px] text-emerald-400 align-top">
                      Zero duplicate client API calls; bundle size -35%
                    </td>
                  </tr>
                  <tr className="border-b border-[#232736]">
                    <td className="py-2.5 px-3 border border-[#232736] font-mono font-bold align-top text-white text-xs whitespace-nowrap">
                      Phase 3 (Days 8–10)
                      <span className="block text-[10px] text-sky-400 font-normal">Edge CDN &amp; Cutover</span>
                    </td>
                    <td className="py-2.5 px-3 border border-[#232736] align-top leading-relaxed">
                      Deploy Cloudflare Cache Rules and edge Brotli compression, execute automated load regression verification, and orchestrate zero-downtime production cutover.
                    </td>
                    <td className="py-2.5 px-3 border border-[#232736] font-mono text-[11px] text-emerald-400 align-top">
                      Cache hit ratio &gt; 85%; EC2 egress costs reduced by 60%
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Recommended Action Pitch */}
            <div className="print-card print-avoid-break p-4 sm:p-5 rounded-xl bg-[#141824] border border-sky-500/40 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-[#232736] pb-3">
                <div>
                  <span className="text-[10px] font-mono text-sky-400 uppercase font-bold block">
                    RECOMMENDED INTERVENTION SPRINT
                  </span>
                  <h4 className="font-tech text-base font-bold text-white mt-0.5">
                    Dedicated 10-Day Architectural Hardening &amp; Refactor Sprint
                  </h4>
                </div>
                <div className="text-right font-mono">
                  <span className="text-2xl font-bold text-sky-300">
                    ${remediationPrice.toLocaleString()}
                  </span>
                  <span className="text-[10px] text-zinc-400 block font-mono">Fixed Scope · Zero Overrun Guarantee</span>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-2 text-xs font-sans text-zinc-300 pt-1">
                <div>✓ Shipped staging branch refactor addressing all 3 identified failure points</div>
                <div>✓ Automated regression test suite &amp; load verification report (&lt; 400ms p95)</div>
                <div>✓ Zero-downtime production cutover orchestration with rollback plan</div>
                <div>✓ 7-day post-cutover monitoring &amp; hypercare warranty support</div>
              </div>
            </div>

            {/* Advisory Methodology & Disclaimer */}
            <div className="print-card print-avoid-break p-3.5 rounded-xl bg-[#10121a] border border-[#232736] space-y-1 text-[#8b92a5]">
              <div className="text-white font-mono font-bold uppercase tracking-wider text-[11px] mb-1">
                AUDIT METHODOLOGY &amp; ADVISORY DISCLAIMER
              </div>
              <p className="font-sans text-xs text-zinc-400 leading-relaxed">
                This memorandum reflects technical observations gathered during an observational due diligence audit. Measurements represent point-in-time sampling of publicly reachable or client-authorized telemetry under current load profiles. Implementation timelines assume continuous client access to staging repositories, infrastructure consoles, and technical points of contact.
              </p>
            </div>

            {/* Signoff / Attestation Block */}
            <div className="print-signature-block print-avoid-break grid grid-cols-2 gap-6 pt-4 border-t border-[#232736]">
              <div className="space-y-2">
                <span className="text-[10px] font-mono text-zinc-500 uppercase block font-bold">TARGET ENTITY SPONSOR:</span>
                <div className="border-b border-zinc-600 pb-1 text-white font-tech font-bold min-h-[22px]">
                  {targetCompany}
                </div>
                <div className="text-[10px] font-mono text-zinc-500 space-y-0.5">
                  <div>NAME: ___________________________</div>
                  <div>TITLE: Executive Sponsor / CTO</div>
                  <div>DATE: ___________________________</div>
                </div>
              </div>
              <div className="space-y-2">
                <span className="text-[10px] font-mono text-zinc-500 uppercase block font-bold">PRINCIPAL SYSTEMS AUDITOR:</span>
                <div className="border-b border-zinc-600 pb-1 text-white font-tech font-bold min-h-[22px]">
                  Systems &amp; Architecture Practice Lead
                </div>
                <div className="text-[10px] font-mono text-zinc-500 space-y-0.5">
                  <div>NAME: ___________________________</div>
                  <div>TITLE: Reliability Practice Lead</div>
                  <div>DATE: {new Date().toLocaleDateString()}</div>
                </div>
              </div>
            </div>

            {isWatermarked && (
              <div className="border-t border-[#232736] pt-3 text-right text-[10px] font-mono text-sky-400/80">
                Generated with Bureau // Teardown Lab · Pro Unlocks Unbranded Vector PDF Export
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
